import { Link } from 'react-router-dom';
import {
  CubeTransparentIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CodeBracketSquareIcon,
  Squares2X2Icon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const features = [
  { icon: CubeTransparentIcon, title: '可视化拖拽', desc: '所见即所得，拖拽式页面设计' },
  { icon: BoltIcon, title: '快速开发', desc: '将开发周期从3-6个月缩短至1-4周' },
  { icon: DevicePhoneMobileIcon, title: '响应式设计', desc: '自动适配PC、平板、手机' },
  { icon: CloudArrowUpIcon, title: '一键发布', desc: '无需服务器配置，一键上线' },
  { icon: ChartBarIcon, title: '数据驱动', desc: '内置数据流引擎，轻松处理业务逻辑' },
  { icon: ShieldCheckIcon, title: '安全可靠', desc: '企业级安全保障，数据加密存储' },
  { icon: CodeBracketSquareIcon, title: '无代码构建复杂逻辑网站', desc: '产品经理功能化操作，无需程序员' },
  { icon: Squares2X2Icon, title: '内置丰富组件', desc: '管理后台、注册登录、内容管理、权限管理、统计分析' },
  { icon: AcademicCapIcon, title: '学习成本低', desc: '功能化操作，简单易学' },
];

// 一级栏目导航
const navButtons = [
  { name: '功能介绍', path: '/features' },
  { name: '产品定价', path: '/pricing' },
  { name: '成果展示', path: '/showcases' },
  { name: '新闻动态', path: '/news' },
  { name: '关于我们', path: '/about' },
];

// 第三方开源软件清单
const openSourceList = [
  // ---- 前端框架与核心 ----
  { name: 'React', version: '18.2', desc: 'UI 用户界面框架', license: 'MIT' },
  { name: 'React DOM', version: '18.2', desc: 'React 浏览器渲染引擎', license: 'MIT' },
  { name: 'React Router', version: '6.20', desc: '前端路由管理', license: 'MIT' },
  { name: 'Zustand', version: '4.5', desc: '前端状态管理', license: 'MIT' },
  { name: 'Immer', version: '10.0', desc: '不可变数据操作', license: 'MIT' },
  { name: 'Axios', version: '1.6', desc: 'HTTP 网络请求客户端', license: 'MIT' },
  // ---- UI 组件与样式 ----
  { name: 'Tailwind CSS', version: '3.3', desc: 'CSS 实用类样式框架', license: 'MIT' },
  { name: 'Lucide React', version: '0.294', desc: '图标组件库', license: 'ISC' },
  { name: '@heroicons/react', version: '2.x', desc: '图标组件库（Heroicons）', license: 'MIT' },
  { name: 'React Hot Toast', version: '2.4', desc: '通知提示组件', license: 'MIT' },
  // ---- 编辑器与安全 ----
  { name: 'TinyMCE', version: '6.8', desc: '富文本编辑器', license: 'MIT' },
  { name: 'DOMPurify', version: '3.x', desc: 'XSS 安全过滤', license: 'Apache-2.0' },
  // ---- 图表 ----
  { name: 'ECharts', version: '5.x', desc: '数据可视化图表库', license: 'Apache-2.0' },
  // ---- 服务端核心 ----
  { name: 'Node.js', version: '18+', desc: '服务端运行时', license: 'MIT' },
  { name: 'Express', version: '4.18', desc: 'Web 服务端框架', license: 'MIT' },
  { name: 'PostgreSQL', version: '15+', desc: '关系型数据库', license: 'PostgreSQL' },
  { name: 'pg', version: '8.11', desc: 'PostgreSQL 数据库驱动', license: 'MIT' },
  // ---- 服务端中间件与工具 ----
  { name: 'cors', version: '2.8', desc: '跨域资源共享中间件', license: 'MIT' },
  { name: 'dotenv', version: '16.3', desc: '环境变量管理', license: 'BSD-2-Clause' },
  { name: 'Helmet', version: '8.1', desc: '安全 HTTP 响应头', license: 'MIT' },
  { name: 'express-rate-limit', version: '7.1', desc: 'API 请求限流', license: 'MIT' },
  { name: 'Multer', version: '1.4', desc: '文件上传处理', license: 'MIT' },
  { name: 'jsonwebtoken', version: '9.0', desc: 'JWT 身份验证', license: 'MIT' },
  { name: 'bcryptjs', version: '2.4', desc: '密码哈希加密（JS 实现）', license: 'MIT' },
  { name: 'bcrypt', version: '6.0', desc: '密码哈希加密（原生实现）', license: 'MIT' },
  { name: 'svg-captcha', version: '1.4', desc: '验证码生成', license: 'MIT' },
  // ---- 构建与开发工具 ----
  { name: 'Vite', version: '5.0', desc: '前端构建工具', license: 'MIT' },
  { name: 'TypeScript', version: '5.3', desc: '类型系统', license: 'Apache-2.0' },
  { name: 'PostCSS', version: '8.4', desc: 'CSS 后处理器', license: 'MIT' },
  { name: 'Autoprefixer', version: '10.4', desc: 'CSS 浏览器前缀自动补全', license: 'MIT' },
  { name: 'ESLint', version: '8.53', desc: '代码规范检查', license: 'MIT' },
  { name: 'pnpm', version: '8+', desc: '包管理工具', license: 'MIT' },
  { name: 'concurrently', version: '8.2', desc: '多进程并行运行', license: 'MIT' },
  { name: 'nodemon', version: '3.0', desc: '开发环境热重启', license: 'MIT' },
];

export default function Home() {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="min-h-[67vh] flex items-center justify-center relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-cyan-900/20"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fadeInUp">
            <span className="gradient-text">零代码</span>
            <span className="text-white">建站平台</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 mb-8 max-w-3xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            让产品经理独立完成网站全生命周期开发，
            <br className="hidden md:block" />
            无需技术团队，无需编写代码
          </p>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-3 gap-8 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text">10x</div>
              <div className="text-gray-400 text-sm">开发效率提升</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text">0</div>
              <div className="text-gray-400 text-sm">代码编写</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text">1-4周</div>
              <div className="text-gray-400 text-sm">项目周期</div>
            </div>
          </div>

          {/* 一级栏目导航按钮 */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
            {navButtons.map((btn) => (
              <Link
                key={btn.path}
                to={btn.path}
                className="px-6 py-3 bg-white/10 text-white rounded-xl text-base font-medium hover:bg-white/20 hover:scale-105 transition-all duration-200 backdrop-blur-sm border border-white/5"
              >
                {btn.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              为什么选择 <span className="gradient-text">NCB</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              我们提供完整的零代码建站解决方案，让您专注于业务本身
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700/50 hover:border-blue-500/50 transition-colors group"
              >
                <div className="w-12 h-12 gradient-bg rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 网站开发说明 Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 md:p-12 rounded-3xl glass">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              网站开发说明
            </h2>

            {/* 说明文字 */}
            <div className="mb-8 text-center">
              <p className="text-gray-300 text-lg leading-relaxed">
                本网站代码由 AI 编程工具撰写，没有一行人工代码。
              </p>
            </div>

            {/* 第三方开源软件表格 */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 text-center">本网站调用的第三方开源软件</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-700/50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-800/80">
                      <th className="px-4 py-3 text-left font-medium text-gray-300">软件名称及版本</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-300">软件功能</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-300">授权类型</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {openSourceList.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-2.5 text-white font-medium whitespace-nowrap">
                          {item.name} <span className="text-blue-400">{item.version}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-400">{item.desc}</td>
                        <td className="px-4 py-2.5">
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-500/10 text-green-400">
                            {item.license}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
